import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { useStages } from "@/lib/stages";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Loader2, Flame, AlarmClock } from "lucide-react";

export default function Dashboard() {
  const { stages: STAGES } = useStages();
  const [loading, setLoading] = useState(true);
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState({ total: 0 });
  const [insights, setInsights] = useState({});

  useEffect(() => {
    Promise.all([
      api.get("/deals", { params: { limit: 1000 } }),
      api.get("/leads", { params: { limit: 1 } }),
      api.get("/insights"),
    ]).then(([d, l, i]) => { setDeals(d.data.items); setLeads(l.data); setInsights(i.data); }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0047FF]" /></div>;

  const open = deals.filter((d) => d.status === "open");
  const won = deals.filter((d) => d.stage === "handover");
  const rejected = deals.filter((d) => d.status === "rejected");
  const conv = deals.length ? Math.round((won.length / deals.length) * 100) : 0;

  const stageData = STAGES.map((s) => ({ name: s.label, value: open.filter((d) => d.stage === s.key).length }));
  const srcCounts = {};
  deals.forEach((d) => { /* source lives on lead; approximate by rep for now skip */ });

  const kpis = [
    { label: "Total Deals", value: deals.length },
    { label: "Open Deals", value: open.length },
    { label: "Total Leads", value: leads.total },
    { label: "Conversion", value: `${conv}%` },
  ];

  const hot = insights.hot_regions?.data || [];
  const stalled = insights.stalled_deals?.data || [];
  const COLORS = ["#0047FF", "#3b6bff", "#6b8dff", "#0F172A", "#64748b"];
  const srcData = hot.slice(0, 5).map((h) => ({ name: h.city, value: h.leads }));

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Your franchise pipeline at a glance." />
      <div className="px-8 py-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <div key={k.label} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="text-xs font-semibold uppercase tracking-[0.05em] text-gray-500">{k.label}</div>
              <div className="text-4xl font-light tracking-tighter text-gray-900 mt-2">{k.value}</div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5">
            <div className="font-heading font-bold text-gray-900 mb-4">Open deals by stage</div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stageData} margin={{ top: 5, right: 10, left: -12, bottom: 34 }}>
                <XAxis dataKey="name" tick={{ fontSize: 9 }} interval={0} angle={-35} textAnchor="end" height={80} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={28} />
                <Tooltip />
                <Bar dataKey="value" fill="#0047FF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="font-heading font-bold text-gray-900 mb-4">Leads by top region</div>
            {srcData.length === 0 ? <div className="text-sm text-gray-400 py-10 text-center">No data yet.</div> : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={srcData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => e.name}>
                    {srcData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="widget-stalled">
            <div className="flex items-center gap-2 font-heading font-bold text-gray-900 mb-3"><AlarmClock className="h-4 w-4 text-rose-500" /> Stalled deals</div>
            {stalled.length === 0 ? <div className="text-sm text-gray-400 py-4">None flagged. {insights.stalled_deals ? "" : "Run Intelligence to compute."}</div> : (
              <div className="space-y-1.5">
                {stalled.slice(0, 6).map((r) => (
                  <div key={r.deal_id} className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate">{r.property} <span className="text-gray-400">· {r.city}</span></span>
                    <span className="text-xs text-rose-600 font-semibold">{r.days_in_stage}d in {r.stage.replace(/_/g, " ")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="widget-hot">
            <div className="flex items-center gap-2 font-heading font-bold text-gray-900 mb-3"><Flame className="h-4 w-4 text-orange-500" /> Hot regions</div>
            {hot.length === 0 ? <div className="text-sm text-gray-400 py-4">No data yet. Run Intelligence to compute.</div> : (
              <div className="space-y-1.5">
                {hot.slice(0, 6).map((r) => (
                  <div key={r.city} className="flex items-center justify-between text-sm">
                    <span className="font-medium">{r.city}</span>
                    <span className="text-xs text-gray-500">score <b className="text-gray-800">{r.score}</b></span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
